import { useEffect } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "./fm";

const SPRING = { stiffness: 90, damping: 16, mass: 0.8 };

export default function TiltCard({ children }) {
  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const rotateY = useSpring(
    useTransform(px, [-0.5, 0.5], [-9, 9]),
    SPRING
  );
  const rotateX = useSpring(
    useTransform(py, [-0.5, 0.5], [7.5, -7.5]),
    SPRING
  );

  const shineX = useTransform(px, [-0.5, 0.5], ["18%", "82%"]);
  const shineY = useTransform(py, [-0.5, 0.5], ["12%", "88%"]);
  const shine = useTransform(
    [shineX, shineY],
    ([x, y]) =>
      `radial-gradient(340px circle at ${x} ${y}, rgba(255,255,255,0.55), rgba(255,255,255,0.08) 42%, transparent 68%)`
  );

  const shadowX = useSpring(
    useTransform(px, [-0.5, 0.5], [18, -18]),
    SPRING
  );
  const shadowS = useSpring(
    useTransform(py, [-0.5, 0.5], [1.06, 0.94]),
    SPRING
  );

  useEffect(() => {
    const onMove = (e) => {
      px.set(e.clientX / window.innerWidth - 0.5);
      py.set(e.clientY / window.innerHeight - 0.5);
    };
    const onLeave = () => {
      px.set(0);
      py.set(0);
    };
    window.addEventListener("pointermove", onMove);
    document.documentElement.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, [px, py]);

  return (
    <div className="relative [perspective:1800px]">
      <motion.div
        aria-hidden="true"
        style={{ x: shadowX, scaleX: shadowS }}
        className="absolute inset-x-10 -bottom-9 h-14 rounded-[100%] bg-[#01040c]/70 blur-2xl"
      />

      <motion.div
        initial={{ opacity: 0, y: 56, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          delay: 0.15,
          duration: 1.1,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <motion.div
          animate={{ y: [0, -9, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.div
            style={{
              rotateX,
              rotateY,
              transformStyle: "preserve-3d",
              willChange: "transform",
            }}
            className="relative"
          >
            <div className="relative rounded-[28px] border border-white/45 bg-white/[0.42] px-6 py-6 shadow-[0_48px_96px_-32px_rgba(2,6,18,0.6),0_0_0_1px_rgba(255,210,31,0.1),inset_0_1px_0_rgba(255,255,255,0.65),inset_0_-1px_0_rgba(4,8,23,0.08)] backdrop-blur-2xl sm:px-9 sm:py-7">
              {children}

              <motion.span
                aria-hidden="true"
                style={{ background: shine, transform: "translateZ(2px)" }}
                className="pointer-events-none absolute inset-0 rounded-[inherit] mix-blend-soft-light"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-sun-400/70 to-transparent"
              />
            </div>

            <motion.span
              aria-hidden="true"
              style={{ transform: "translateZ(72px)" }}
              className="animate-float-slow absolute -top-5 -right-4 h-14 w-14 rounded-full bg-gradient-to-br from-sun-300 via-sun-400 to-sun-500 shadow-[0_18px_36px_-10px_rgba(245,183,10,0.75),inset_0_-6px_12px_rgba(180,120,0,0.35),inset_0_6px_10px_rgba(255,255,255,0.6)]"
            />
            <motion.span
              aria-hidden="true"
              style={{ transform: "translateZ(48px)" }}
              className="animate-float-slow absolute -bottom-4 -left-5 h-9 w-9 rounded-full bg-gradient-to-br from-azure-300 via-azure-500 to-navy-700 shadow-[0_16px_32px_-10px_rgba(46,107,255,0.8),inset_0_-5px_10px_rgba(4,10,30,0.4),inset_0_5px_8px_rgba(255,255,255,0.45)] [animation-delay:-3.5s]"
            />
            <motion.span
              aria-hidden="true"
              style={{ transform: "translateZ(28px)" }}
              className="absolute top-1/2 -left-3 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-white/90 shadow-[0_0_14px_rgba(255,255,255,0.9)]"
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
